/** Invite a single installer from Manage Installers — sends the set-your-password email via /staff/create-account. */
import React, { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { formatApiError } from '@/utils/apiError';

const AREAS = [['probound', 'ProBound'], ['proepoxy', 'ProEpoxy'], ['prodecor', 'ProDecor'], ['protraining', 'ProTraining']];
const EMPTY = { name: '', email: '', company_name: '', phone: '', access: { probound: true, proepoxy: false, prodecor: false, protraining: false } };

export default function InviteInstallerDialog({ open, onClose, onInvited }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/create-account`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, role: 'installer' }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not send invite');
      toast.success(`Invite sent to ${form.email.trim()} — they set their own password from the link (valid 7 days)`);
      setForm(EMPTY); onInvited?.(); onClose();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="invite-installer-dialog">
        <DialogHeader><DialogTitle>Add an installer</DialogTitle><DialogDescription>They'll get an email with a one‑time link to set their password. Names are tidied to Title Case automatically.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label htmlFor="ii-name">Full name *</Label><Input id="ii-name" value={form.name} onChange={set('name')} placeholder="e.g. Sam Taylor" required data-testid="ii-name" /></div>
          <div><Label htmlFor="ii-email">Email *</Label><Input id="ii-email" type="email" value={form.email} onChange={set('email')} placeholder="sam@example.co.uk" required data-testid="ii-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="ii-company">Company</Label><Input id="ii-company" value={form.company_name} onChange={set('company_name')} data-testid="ii-company" /></div>
            <div><Label htmlFor="ii-phone">Mobile</Label><Input id="ii-phone" value={form.phone} onChange={set('phone')} placeholder="07xxx xxxxxx" inputMode="tel" data-testid="ii-phone" /></div>
          </div>
          <div>
            <Label>Unlock straight away</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {AREAS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm border rounded-md px-2 py-1.5 cursor-pointer hover:bg-gray-50">
                  <Checkbox checked={!!form.access[k]} onCheckedChange={(v) => setForm((f) => ({ ...f, access: { ...f.access, [k]: !!v } }))} data-testid={`ii-access-${k}`} /> {l}
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="ii-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#7A0B7A] hover:bg-[#5e085e] text-white font-bold" data-testid="ii-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send invite</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
